import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { Editor } from "@tinymce/tinymce-react";
import { Button, Form, Card, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
import {
  SingleImageDropzone,
  MultipleImageDropzone,
} from "../DropzoneComponents";

const ProductEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const API = process.env.REACT_APP_API_BASE_URL;

  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [features, setFeatures] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showBrand, setShowBrand] = useState(false);
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

  const [existingMain, setExistingMain] = useState(null);
  const [existingGallery, setExistingGallery] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    title: "",
    brand_id: "",
    barcode: "",
    price: "",
    discount_price: "",
    stock: {},
    category_ids: [],
    short_description: "",
    content: "",
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    status: "aktif",
    main_image: null,
    gallery: [],
    features: {},
    currency_id: "",
  });

  const onlyNumbers = (v) => v.replace(/[^0-9,.]/g, "");

  //  Setting fetch
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    axios
      .get(`${API}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setSettings(res.data));
  }, []);
  useEffect(() => {
    axios
      .get(`${API}/api/currencies/active`)
      .then((res) => setCurrencies(res.data));
  }, []);

  //  Load Product + Related data
  //  Load Product + Related data
  useEffect(() => {
    (async () => {
      const prodRes = await axios.get(`${API}/api/products/${id}`);
      const product = prodRes.data;

      setFormData((p) => ({
        ...p,
        title: product.title,
        barcode: product.barcode,
        price: product.price,
        discount_price: product.discount_price,
        stock: product.stock,
        category_ids: product.categories,
        short_description: product.short_description,
        content: product.content,
        seo_title: product.seo_title,
        seo_description: product.seo_description,
        seo_keywords: product.seo_keywords,
        status: product.status,
        brand_id: product.brand_id,
        currency_id: product.currency_id,
        features: product.features || {},
        // ✅ Gallery URL'lerini API base URL ile birleştir
        gallery: (product.gallery || []).map((url) => {
          // Eğer URL zaten tam path ise olduğu gibi bırak
          if (url.startsWith("http")) return url;
          // Değilse API base URL'i ekle
          return `${API}${url}`;
        }),
      }));

      setExistingMain(product.main_image);

      const catTree = await axios.get(`${API}/api/stock-categories/tree`);
      const flatCats = flatten(catTree.data);
      setCategories(flatCats);

      const selectedCats = flatCats.filter((c) =>
        product.categories.includes(c.value),
      );
      setShowBrand(selectedCats.some((c) => c.brand_option === "evet"));

      const featRes = await axios.post(`${API}/api/features/by-categories`, {
        category_ids: product.categories,
      });
      setFeatures(featRes.data);

      const brandRes = await axios.get(`${API}/api/stock-brands/active`);
      setBrands(brandRes.data);

      const whRes = await axios.get(`${API}/api/warehouses/active`);
      setWarehouses(whRes.data);
    })();
  }, [id, API]); // ✅ API'yi dependency'e ekle

  const flatten = (items) => {
    let out = [];
    items.forEach((i) => {
      out.push({
        value: i.value,
        label: i.label,
        brand_option: i.brand_option,
      });
      if (i.children) out = [...out, ...flatten(i.children)];
    });
    return out;
  };

  const handleFeatureChange = (id, val) => {
    setFormData({
      ...formData,
      features: {
        ...formData.features,
        [id]: val
          ? Array.isArray(val)
            ? val.map((x) => x.value)
            : [val.value]
          : [],
      },
    });
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const payload = new FormData();

      Object.entries(formData).forEach(([key, val]) => {
        if (["main_image", "gallery"].includes(key)) return;

        if (key === "category_ids") {
          payload.append("categories", JSON.stringify(val || []));
          return;
        }

        if (key === "brand_id" && !val) return;

        if (key === "currency_id") {
          payload.append("currency_id", val || "");
          return;
        }

        payload.append(
          key,
          typeof val === "object" ? JSON.stringify(val) : val,
        );
      });

      if (formData.main_image) {
        payload.append("main_image", formData.main_image);
      }

      // ✅ 1) Eski galeriler
      const existingGallery = formData.gallery.filter(
        (g) => typeof g === "string",
      );
      payload.append("existing_gallery", JSON.stringify(existingGallery));

      // ✅ 2) Yeni galeriler
      const newGallery = formData.gallery.filter((g) => g instanceof File);
      newGallery.forEach((file) => payload.append("gallery", file));

      await axios.put(`${API}/api/products/${id}`, payload);
      toast.success(getText("PRODUCT_SAVED"));
      navigate("/products/table");
    } catch (err) {
      toast.error(getText("SETTINGS_FETCH_ERROR"));
    } finally {
      setLoading(false);
    }
  };
  const validateForm = () => {
    const newErrors = {};

    // Barkod
    if (!formData.barcode?.trim()) {
      newErrors.barcode = getText("FIELD_REQUIRED");
    }

    // Fiyat
    if (!formData.price?.trim()) {
      newErrors.price = getText("FIELD_REQUIRED");
    }

    // Para birimi
    if (!formData.currency_id) {
      newErrors.currency_id = getText("FIELD_REQUIRED");
    }
    if (!formData.main_image && !existingMain) {
      newErrors.main_image = getText("FIELD_REQUIRED");
    }

    // Marka sadece showBrand TRUE ise
    if (showBrand && !formData.brand_id) {
      newErrors.brand_id = getText("FIELD_REQUIRED");
    }

    // Ürün Başlığı
    if (!formData.title?.trim()) {
      newErrors.title = getText("FIELD_REQUIRED");
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  return (
    <div className="container mt-4">
      {/* Selected categories preview */}
      <Card className="shadow-sm p-3 mb-3">
        <strong>{getText("CATEGORIES_LABEL")}:</strong>
        <div className="mt-2">
          {formData.category_ids.map((cid) => {
            const c = categories.find((x) => x.value === cid);
            return (
              <span key={cid} className="badge bg-primary me-2 mb-2">
                {c?.label}
              </span>
            );
          })}
        </div>
      </Card>

      {/* CARD 1 — Ürün Bilgileri */}
      <Card className="shadow-sm p-4 mb-4">
        <h5>{getText("PRODUCT_INFORMATION_TITLE")}</h5>

        <Row className="mb-3 g-3 align-items-end">
          {showBrand && (
            <Col xs={12} md={3}>
              <Form.Label>
                {getText("BRAND_LABEL")} <span className="text-danger">*</span>
              </Form.Label>
              <Select
                value={
                  formData.brand_id
                    ? {
                        value: formData.brand_id,
                        label: brands.find((b) => b.id === formData.brand_id)
                          ?.title,
                      }
                    : null
                }
                options={brands.map((b) => ({ value: b.id, label: b.title }))}
                onChange={(v) =>
                  setFormData({ ...formData, brand_id: v?.value || "" })
                }
              />
              {errors.brand_id && (
                <div
                  className="text-danger mt-1"
                  style={{ fontSize: "0.85rem" }}
                >
                  {errors.brand_id}
                </div>
              )}
            </Col>
          )}

          <Col xs={12} md={showBrand ? 3 : 3}>
            <Form.Label>
              {getText("BARCODE_LABEL")} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              value={formData.barcode}
              disabled
              onChange={(e) =>
                setFormData({ ...formData, barcode: e.target.value })
              }
            />
            {errors.barcode && (
              <div className="text-danger mt-1" style={{ fontSize: "0.85rem" }}>
                {errors.barcode}
              </div>
            )}
          </Col>

          <Col xs={12} md={showBrand ? 2 : 3}>
            <Form.Label>
              {getText("PRICE_LABEL")} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              value={formData.price}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  price: onlyNumbers(e.target.value),
                })
              }
            />
            {errors.price && (
              <div className="text-danger mt-1" style={{ fontSize: "0.85rem" }}>
                {errors.price}
              </div>
            )}
          </Col>

          <Col xs={12} md={showBrand ? 2 : 3}>
            <Form.Label>{getText("DISCOUNT_PRICE_LABEL")}</Form.Label>
            <Form.Control
              value={formData.discount_price}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  discount_price: onlyNumbers(e.target.value),
                })
              }
            />
          </Col>

          <Col xs={12} md={showBrand ? 2 : 3}>
            <Form.Label>
              {getText("CURRENCY_LABEL")} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={formData.currency_id || ""}
              onChange={(e) =>
                setFormData({ ...formData, currency_id: e.target.value })
              }
            >
              <option value="">{getText("SELECT_PLACEHOLDER")}</option>
              {/** currencies listeni önceden çektiğini varsayıyorum */}
              {currencies?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code}
                </option>
              ))}
            </Form.Select>
            {errors.currency_id && (
              <div className="text-danger mt-1" style={{ fontSize: "0.85rem" }}>
                {errors.currency_id}
              </div>
            )}
          </Col>
        </Row>

        <h6>{getText("WAREHOUSE_STOCK_TITLE")}</h6>
        {warehouses.map((w) => (
          <div key={w.id} className="mb-2">
            <small className="fw-bold">{w.title}</small>
            <Form.Control
              inputMode="numeric"
              pattern="[0-9]*"
              defaultValue={formData.stock?.[w.id] || ""}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
              }}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  stock: {
                    ...formData.stock,
                    [w.id]: onlyNumbers(e.target.value),
                  },
                })
              }
            />
          </div>
        ))}
      </Card>

      {/* CARD 2 — Görseller & Açıklamalar */}
      <Card className="shadow-sm p-4 mb-4">
        <h5>{getText("PRODUCT_DESCRIPTIONS_TITLE")}</h5>

        {/* Title */}
        <Form.Label>
          {getText("PRODUCT_TITLE_LABEL")}
          <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          defaultValue={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="mb-3"
        />
        {errors.title && (
          <div className="text-danger mt-1" style={{ fontSize: "0.85rem" }}>
            {errors.title}
          </div>
        )}

        {/* Main Image */}
        <Form.Label>
          {getText("LIST_IMAGE_LABEL")} <span className="text-danger">*</span>
        </Form.Label>
        <SingleImageDropzone
          fieldName="main_image"
          fieldValue={formData.main_image || existingMain}
          setFieldValues={(fn) => {
            const res =
              typeof fn === "function"
                ? fn({ main_image: formData.main_image })
                : fn;
            if (res.main_image === null) {
              setExistingMain(null);
              setFormData((p) => ({
                ...p,
                main_image: null,
                delete_main_image: true,
              }));
            } else {
              setExistingMain(null);
              setFormData((p) => ({ ...p, main_image: res.main_image }));
            }
          }}
          maxWidth={Number(settings?.product_list_image_width)}
          maxHeight={Number(settings?.product_list_image_height)}
        />
        {errors.main_image && (
          <div className="text-danger mt-1" style={{ fontSize: "0.85rem" }}>
            {errors.main_image}
          </div>
        )}

        <Form.Label className="mt-3">
          {getText("PRODUCT_GALLERY_LABEL")}
        </Form.Label>

        <MultipleImageDropzone
          fieldName="gallery"
          fieldValue={formData.gallery}
          setFieldValues={(u) => {
            const files =
              typeof u === "function"
                ? u({ gallery: formData.gallery }).gallery
                : u.gallery;
            setFormData((p) => ({ ...p, gallery: files }));
          }}
          maxWidth={Number(settings?.product_gallery_image_width)}
          maxHeight={Number(settings?.product_gallery_image_height)}
        />

        {/* Short description */}
        <Form.Label>{getText("SHORT_DESCRIPTION_LABEL")}</Form.Label>
        <Form.Control
          as="textarea"
          defaultValue={formData.short_description}
          onChange={(e) =>
            setFormData({ ...formData, short_description: e.target.value })
          }
          rows={3}
          className="mb-3"
        />

        {/* Content */}
        <Form.Label>{getText("DETAILED_CONTENT_LABEL")}</Form.Label>
        <Editor
          tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
          value={formData.content}
          onEditorChange={(content) => setFormData({ ...formData, content })}
          init={{
            promotion: false,
            height: 400,
            plugins: [
              "advlist",
              "autolink",
              "link",
              "image",
              "lists",
              "charmap",
              "preview",
              "anchor",
              "pagebreak",
              "searchreplace",
              "wordcount",
              "visualblocks",
              "code",
              "fullscreen",
              "insertdatetime",
              "media",
              "table",
              "emoticons",
              "help",
            ],
            toolbar:
              "undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify | " +
              "bullist numlist outdent indent | link image | " +
              "print preview media fullscreen code | forecolor backcolor emoticons | help",
            content_style:
              "body { font-family:Helvetica,Arial,sans-serif; font-size:16px }",
            image_advtab: true,
            automatic_uploads: false,
            images_upload_url: `${process.env.REACT_APP_API_BASE_URL}/api/module-data/upload`,
            images_upload_handler: null,
          }}
        />
      </Card>

      {/*CARD 3 — Özellikler*/}
      {features.length > 0 && (
        <Card className="shadow-sm p-4 mb-4">
          <h5 className="mb-4">{getText("PRODUCT_FEATURES_TITLE")}</h5>

          {features.map((f) => (
            <div key={f.id} className="mb-4 p-3 border rounded">
              {/* Başlık + Temizle */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="fw-bold">
                  {f.title}
                  {f.is_required && <span style={{ color: "red" }}> *</span>}
                </label>

                {/* Zorunlu değilse: Seçimi Kaldır */}
                {!f.is_required && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        features: { ...formData.features, [f.id]: [] },
                      })
                    }
                  >
                    {getText("CLEAR_SELECTION_BUTTON")}
                  </button>
                )}
              </div>

              {/* SELECT MODE */}
              {f.input_type === "select" ? (
                <Select
                  isMulti={Boolean(f.is_multiple)}
                  options={f.values.map((v) => ({
                    value: v.id,
                    label: v.value,
                  }))}
                  placeholder={getText("SELECT_PLACEHOLDER")}
                  menuPortalTarget={document.body} // ✔ dropdown taşmasın
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  value={
                    formData.features[f.id]
                      ? f.values
                          .filter((v) => formData.features[f.id].includes(v.id))
                          .map((v) => ({ value: v.id, label: v.value }))
                      : []
                  }
                  onChange={(v) => handleFeatureChange(f.id, v)}
                />
              ) : (
                // RADIO / CHECKBOX MODE
                <div className="d-flex flex-wrap gap-3">
                  {/* Değer listesi */}
                  {f.values.map((v) => {
                    const selected = formData.features[f.id]?.includes(v.id);

                    return (
                      <div key={v.id} className="form-check">
                        <input
                          type={f.is_multiple ? "checkbox" : "radio"}
                          name={`feature-${f.id}`}
                          className="form-check-input"
                          checked={selected}
                          onChange={() => {
                            let newValues = [];

                            if (f.is_multiple) {
                              // Çoklu seçim
                              newValues = selected
                                ? formData.features[f.id].filter(
                                    (x) => x !== v.id,
                                  )
                                : [...(formData.features[f.id] || []), v.id];
                            } else {
                              // Tekli seçim
                              newValues = selected ? [] : [v.id];
                            }

                            setFormData({
                              ...formData,
                              features: {
                                ...formData.features,
                                [f.id]: newValues,
                              },
                            });
                          }}
                        />
                        <label className="form-check-label">{v.value}</label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      <Card className="shadow-sm p-4 mb-4">
        <h5>{getText("SEO_INFORMATION_TITLE")}</h5>

        <Form.Label>{getText("SEO_TITLE_LABEL")}</Form.Label>
        <Form.Control
          defaultValue={formData.seo_title}
          onChange={(e) =>
            setFormData({ ...formData, seo_title: e.target.value })
          }
          className="mb-2"
        />

        <Form.Label>{getText("SEO_DESCRIPTION_LABEL")}</Form.Label>
        <Form.Control
          as="textarea"
          defaultValue={formData.seo_description}
          onChange={(e) =>
            setFormData({ ...formData, seo_description: e.target.value })
          }
          className="mb-2"
        />

        <Form.Label>{getText("SEO_KEYWORDS_LABEL")}</Form.Label>
        <Form.Control
          defaultValue={formData.seo_keywords}
          onChange={(e) =>
            setFormData({ ...formData, seo_keywords: e.target.value })
          }
          className="mb-2"
        />

        <Form.Label>{getText("STATUS_LABEL")}</Form.Label>
        <Form.Select
          defaultValue={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          className="mb-3"
        >
          <option value="aktif">{getText("STATUS_ACTIVE")}</option>
          <option value="pasif">{getText("STATUS_PASSIVE")}</option>
        </Form.Select>
        <div className="d-flex justify-content-center gap-2">
          <Button disabled={loading} onClick={handleSubmit}>
            {getText("SAVE_BUTTON")}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ProductEdit;
